import { formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { CashLine, CashPeriod, CashRow, CashWeekly } from '@/services/http/cash'

/**
 * Haftalık Nakit ekranının saf yardımcıları (docs/NAKIT-PLANI-PLAN.md Faz 1). Satırların sırası ve
 * değerleri backend motorundan gelir; burada yalnız bölümleme, etiket, renk ve hücre kalemleri.
 */

export type CashSectionKey = keyof typeof messages.cash.sections

/** Excel Haftalik_Nakit düzeni: her bölüm, sıradaki ilk satır anahtarından başlar. */
const SECTION_STARTS: [string, CashSectionKey][] = [
  ['banka_acilis', 'bank'],
  ['giris_tahsildeki_cek', 'inflows'],
  ['cikis_tedarikci_grup_disi', 'outflows'],
  ['kasa_acilis', 'cash'],
  ['net_akis', 'summary'],
  ['portfoy_acilis', 'cheques'],
]

export interface CashSection {
  key: CashSectionKey
  rows: CashRow[]
}

export function cashSections(rows: CashRow[]): CashSection[] {
  const starts = new Map(SECTION_STARTS)
  const sections: CashSection[] = []
  for (const row of rows) {
    const start = starts.get(row.key)
    if (start || sections.length === 0) sections.push({ key: start ?? 'bank', rows: [] })
    sections[sections.length - 1]!.rows.push(row)
  }
  return sections
}

/** Sabit satırın etiketi `messages`'tan; tür satırı (`giris:<tür>`) türün adıyla görünür. */
export function cashRowLabel(key: string): string {
  return messages.cash.rows[key] ?? key.replace(/^(giris|cikis):/, '')
}

export type CashTone = 'negative' | 'belowMin' | undefined

/** Eksi her değer kırmızı; dönem sonu nakit minimum seviyenin altındaysa turuncu. */
export function cashTone(row: CashRow, value: number, minCash: number): CashTone {
  if (value < -0.005) return 'negative'
  if (row.key === 'donem_sonu_nakit' && value < minCash) return 'belowMin'
  return undefined
}

/** Dönem sonu nakdi eksi olan dönemler (başlıkta kırmızı). */
export function negativePeriods(weekly: CashWeekly): Set<number> {
  const row = weekly.rows.find((r) => r.key === 'donem_sonu_nakit')
  return new Set(row?.values.flatMap((v, i) => (v < -0.005 ? [i] : [])) ?? [])
}

/** Tıklanabilir hücre: akış ya da toplam satırı (bakiye satırının kalemi yok). */
export function isDrillable(row: CashRow): boolean {
  return row.kind !== 'balance' && row.key !== 'ciro_fark'
}

/** Hücrenin kaynak kalemleri; toplam satırı alt satırlarının kalemlerini açar. */
export function cellLines(weekly: CashWeekly, rowKey: string, period: number): CashLine[] {
  const keys = new Set(weekly.row_children[rowKey] ?? [rowKey])
  return weekly.lines.filter((line) => line.period === period && keys.has(line.row))
}

/**
 * Net akışta çıkışlar eksi sayılır (motor: giriş − çıkış); yan paneldeki toplam hücreyle aynı
 * çıksın diye çıkış kalemi işaretli döner.
 */
export function signedAmount(rowKey: string, line: CashLine): number {
  if (rowKey !== 'net_akis') return line.amount
  const outflow =
    line.row.startsWith('cikis') || line.row === 'kasa_tedarikci' || line.row === 'kasa_erp_disi'
  return outflow ? -line.amount : line.amount
}

const two = (n: string) => n.padStart(2, '0')

/** `2026-10-27` → `27.10`. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${two(d ?? '')}.${two(m ?? '')}`
}

/** `2026-10-27` → `27.10.2026`; yıl dönümünde haftalar karışmasın diye hafta başlığında yıl yazılır. */
export function dateWithYear(iso: string): string {
  const [y] = iso.split('-')
  return `${shortDate(iso)}.${y ?? ''}`
}

/** Nakit ekranlarında tutar tam TL (kuruş yok); tam değer hücrenin başlığında. */
export function formatCashAmount(value: number): string {
  return formatDecimal(value, 0)
}

/** Tablo hücresi: sıfır tire olarak görünür. */
export function formatCashCell(value: number): string {
  return Math.abs(value) < 0.005 ? '–' : formatCashAmount(value)
}

/** Kuruşlu tam değer: hücre başlığı ve ekran okuyucu etiketi. */
export function formatCashExact(value: number): string {
  return formatDecimal(value, 2)
}

/** Hafta: "Salı 27.10.2026" + aralık; ay: "Kasım 2026". */
export function periodLabel(period: CashPeriod): { title: string; range: string } {
  if (period.kind === 'ay') {
    const [y, m] = period.start.split('-')
    return {
      title: `${messages.period.months[Number(m) - 1]} ${y}`,
      range: `${shortDate(period.start)}–${shortDate(period.end)}`,
    }
  }
  return {
    title: messages.cash.weekly.week(dateWithYear(period.end)),
    range: `${shortDate(period.start)}–${shortDate(period.end)}`,
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * Düzenli kalemin ödeme günü önizlemesi (backend `tahakkuk_odeme_tarihi` ile aynı kural):
 * tahakkuk ayına türün ay farkı eklenir, ayın türün (ya da satırın) günü alınır; ayda o gün yoksa
 * son gün. Başlangıçtan önceye düşerse `null` (ödenmiş sayılır). Gün yoksa da `null`.
 */
export function previewPaymentDate(
  period: string,
  rowDay: number | null | undefined,
  type: { day?: number | null; month_offset?: number } | undefined,
  start: string,
): string | null {
  const day = rowDay ?? type?.day ?? null
  const match = /^(\d{4})-(\d{2})/.exec(period)
  if (!match || day === null) return null
  const total = Number(match[1]) * 12 + Number(match[2]) - 1 + (type?.month_offset ?? 1)
  const year = Math.floor(total / 12)
  const month = (total % 12) + 1
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const iso = `${year}-${pad(month)}-${pad(Math.min(day, last))}`
  return iso < start ? null : iso
}
